import { WallShape } from "../types";
import { getWallControlPoint } from "./bezier";
import { getWallSegments } from "./wall";
import { DEFAULT_WALL_COLOR, DEFAULT_WALL_THICKNESS } from "../components/map/defines";

interface Point {
  x: number;
  y: number;
}

export interface JunctionFill {
  center: Point;
  points: Point[];
  color: string;
}

const JUNCTION_EPSILON = 1;
const MITER_LIMIT = 4;

/** Check if a wall has solid material (no door gap) at the given endpoint */
const wallHasMaterialAtEndpoint = (
  wall: WallShape,
  endpoint: "start" | "end"
): boolean => {
  if (!wall.doors || wall.doors.length === 0) return true;
  const dx = wall.x2 - wall.x;
  const dy = wall.y2 - wall.y;
  const wallLength = Math.sqrt(dx * dx + dy * dy);
  if (wallLength === 0) return true;
  const segments = getWallSegments(wall.doors, wallLength);
  if (segments.length === 0) return false;
  if (endpoint === "start") {
    return segments[0][0] < 0.5;
  }
  return segments[segments.length - 1][1] > wallLength - 0.5;
};

/** Get the unit direction vector pointing away from an endpoint along the wall */
export const getWallDirectionAtEndpoint = (
  wall: WallShape,
  endpoint: "start" | "end"
): Point => {
  const curvature = wall.curvature || 0;
  const curvatureCenter = wall.curvatureCenter ?? 0.5;

  if (curvature === 0) {
    // Straight wall: direction is simply along the wall
    const dx = wall.x2 - wall.x;
    const dy = wall.y2 - wall.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return { x: 1, y: 0 };
    if (endpoint === "start") {
      return { x: dx / len, y: dy / len };
    }
    return { x: -dx / len, y: -dy / len };
  }

  // Curved wall: use bezier tangent
  const { cx, cy } = getWallControlPoint(
    wall.x,
    wall.y,
    wall.x2,
    wall.y2,
    curvature,
    curvatureCenter
  );

  if (endpoint === "start") {
    // B'(0) = 2(C - P0)
    const tx = 2 * (cx - wall.x);
    const ty = 2 * (cy - wall.y);
    const len = Math.sqrt(tx * tx + ty * ty);
    if (len === 0) return { x: 1, y: 0 };
    return { x: tx / len, y: ty / len };
  }

  // B'(1) = 2(P1 - C)
  const tx = 2 * (wall.x2 - cx);
  const ty = 2 * (wall.y2 - cy);
  const len = Math.sqrt(tx * tx + ty * ty);
  if (len === 0) return { x: -1, y: 0 };
  // Negate: direction pointing away from junction (back along wall)
  return { x: -tx / len, y: -ty / len };
};

/** Find intersection of two lines: p1 + t*d1 and p2 + s*d2 */
const lineLineIntersection = (
  p1: Point,
  d1: Point,
  p2: Point,
  d2: Point
): Point | null => {
  const det = d1.y * d2.x - d1.x * d2.y;
  if (Math.abs(det) < 1e-10) return null;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const t = (dy * d2.x - dx * d2.y) / det;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
};

/** Compute the filled polygon for a junction where multiple wall endpoints meet */
const computeJunctionFill = (
  center: Point,
  wallEndpoints: { wall: WallShape; endpoint: "start" | "end" }[]
): JunctionFill | null => {
  if (wallEndpoints.length < 2) return null;

  const wallData = wallEndpoints.map(({ wall, endpoint }) => {
    const dir = getWallDirectionAtEndpoint(wall, endpoint);
    const halfThick = (wall.thickness || DEFAULT_WALL_THICKNESS) / 2;
    const angle = Math.atan2(dir.y, dir.x);
    const color = wall.color || DEFAULT_WALL_COLOR;
    return { dir, halfThick, angle, color };
  });

  // Sort by direction angle
  wallData.sort((a, b) => a.angle - b.angle);

  // Color majority vote
  const colorCounts = new Map<string, number>();
  for (const { color } of wallData) {
    colorCounts.set(color, (colorCounts.get(color) || 0) + 1);
  }
  let bestColor = DEFAULT_WALL_COLOR;
  let bestCount = 0;
  for (const [color, count] of colorCounts) {
    if (count > bestCount) {
      bestCount = count;
      bestColor = color;
    }
  }

  const n = wallData.length;
  const allPoints: Point[] = [];

  for (let i = 0; i < n; i++) {
    const next = (i + 1) % n;
    const { dir: dI, halfThick: htI } = wallData[i];
    const { dir: dNext, halfThick: htNext } = wallData[next];

    // Wall i's CCW perpendicular offset point (faces sector between wall i and wall next)
    const ccwOffset: Point = {
      x: center.x + (-dI.y) * htI,
      y: center.y + dI.x * htI
    };
    allPoints.push(ccwOffset);

    // Wall next's CW perpendicular offset point (faces same sector)
    const cwOffsetNext: Point = {
      x: center.x + dNext.y * htNext,
      y: center.y + (-dNext.x) * htNext
    };

    // Miter point: intersection of the two wall edge lines
    const miter = lineLineIntersection(ccwOffset, dI, cwOffsetNext, dNext);
    if (miter) {
      const mDx = miter.x - center.x;
      const mDy = miter.y - center.y;
      const miterDist = Math.sqrt(mDx * mDx + mDy * mDy);
      const maxMiter = MITER_LIMIT * Math.max(htI, htNext);
      if (miterDist <= maxMiter) {
        allPoints.push(miter);
      }
    }

    allPoints.push(cwOffsetNext);
  }

  // Sort all points by angle around center for a correct convex polygon
  allPoints.sort((a, b) => {
    const angleA = Math.atan2(a.y - center.y, a.x - center.x);
    const angleB = Math.atan2(b.y - center.y, b.x - center.x);
    return angleA - angleB;
  });

  // Remove near-duplicate points
  const points: Point[] = [allPoints[0]];
  for (let i = 1; i < allPoints.length; i++) {
    const prev = points[points.length - 1];
    const curr = allPoints[i];
    const pDx = curr.x - prev.x;
    const pDy = curr.y - prev.y;
    if (pDx * pDx + pDy * pDy > 0.01) {
      points.push(curr);
    }
  }

  return { center, points, color: bestColor };
};

/** Get junction fills for a specific wall by checking its endpoints against all other walls */
export const getJunctionsForWall = (
  wall: WallShape,
  allWalls: WallShape[],
  epsilon: number = JUNCTION_EPSILON
): JunctionFill[] => {
  const fills: JunctionFill[] = [];
  const eps2 = epsilon * epsilon;

  const endpoints: { point: Point; endpoint: "start" | "end" }[] = [
    { point: { x: wall.x, y: wall.y }, endpoint: "start" },
    { point: { x: wall.x2, y: wall.y2 }, endpoint: "end" }
  ];

  for (const { point, endpoint } of endpoints) {
    if (!wallHasMaterialAtEndpoint(wall, endpoint)) continue;

    const group: { wall: WallShape; endpoint: "start" | "end" }[] = [
      { wall, endpoint }
    ];

    for (const other of allWalls) {
      if (other.id === wall.id) continue;

      const dxStart = other.x - point.x;
      const dyStart = other.y - point.y;
      if (dxStart * dxStart + dyStart * dyStart <= eps2 && wallHasMaterialAtEndpoint(other, "start")) {
        group.push({ wall: other, endpoint: "start" });
      }

      const dxEnd = other.x2 - point.x;
      const dyEnd = other.y2 - point.y;
      if (dxEnd * dxEnd + dyEnd * dyEnd <= eps2 && wallHasMaterialAtEndpoint(other, "end")) {
        group.push({ wall: other, endpoint: "end" });
      }
    }

    if (group.length >= 2) {
      const fill = computeJunctionFill(point, group);
      if (fill) fills.push(fill);
    }
  }

  return fills;
};

/** Find all unique junction fills across all walls (for SVG batch rendering) */
export const findWallJunctions = (
  walls: WallShape[],
  epsilon: number = JUNCTION_EPSILON
): JunctionFill[] => {
  const eps2 = epsilon * epsilon;
  const junctions: JunctionFill[] = [];
  // Track which junction centers we've already processed (avoid duplicates)
  const processed: Point[] = [];

  for (const wall of walls) {
    const wallEndpoints: Point[] = [
      { x: wall.x, y: wall.y },
      { x: wall.x2, y: wall.y2 }
    ];

    for (const point of wallEndpoints) {
      // Skip if we already processed a junction at this point
      if (processed.some((p) => {
        const dx = p.x - point.x;
        const dy = p.y - point.y;
        return dx * dx + dy * dy <= eps2;
      })) {
        continue;
      }

      const group: { wall: WallShape; endpoint: "start" | "end" }[] = [];

      // Check this wall's endpoint
      const dxStart = wall.x - point.x;
      const dyStart = wall.y - point.y;
      if (dxStart * dxStart + dyStart * dyStart <= eps2 && wallHasMaterialAtEndpoint(wall, "start")) {
        group.push({ wall, endpoint: "start" });
      }
      const dxEnd = wall.x2 - point.x;
      const dyEnd = wall.y2 - point.y;
      if (dxEnd * dxEnd + dyEnd * dyEnd <= eps2 && wallHasMaterialAtEndpoint(wall, "end")) {
        group.push({ wall, endpoint: "end" });
      }

      // Check all other walls
      for (const other of walls) {
        if (other.id === wall.id) continue;

        const odxStart = other.x - point.x;
        const odyStart = other.y - point.y;
        if (odxStart * odxStart + odyStart * odyStart <= eps2 && wallHasMaterialAtEndpoint(other, "start")) {
          group.push({ wall: other, endpoint: "start" });
        }

        const odxEnd = other.x2 - point.x;
        const odyEnd = other.y2 - point.y;
        if (odxEnd * odxEnd + odyEnd * odyEnd <= eps2 && wallHasMaterialAtEndpoint(other, "end")) {
          group.push({ wall: other, endpoint: "end" });
        }
      }

      if (group.length >= 2) {
        const fill = computeJunctionFill(point, group);
        if (fill) {
          junctions.push(fill);
          processed.push(point);
        }
      }
    }
  }

  return junctions;
};
